import * as React from 'react'
import { Store, Check } from 'lucide-react'
import {
  useOwnVendor,
  useUpdateOwnVendor,
  useSetOwnVendorServices,
  useSetOwnVendorSearchListing,
  useVendorPeople,
} from '@/hooks/useVendors'
import { useAuth } from '@/auth/AuthContext'
import { CoverPhotoField } from '@/components/CoverPhotoField'
import { requestErrorMessage } from '@/helpers/apiClient'
import { updateVendorInputSchema } from '@lib/input/vendor.input'
import type { VendorDto } from '@lib/dto/vendor.dto'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormActions } from '@/components/ui/form-actions'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'
import { useSetQuoteRecipient } from '@/hooks/useQuoteRequests'
import { VendorServicesPicker } from '@/components/VendorServicesPicker'
/** The details form, mounted once the vendor has loaded so it seeds from it. */
function VendorSettings({ vendor }: { vendor: VendorDto }) {
  const updateVendor = useUpdateOwnVendor()
  const [name, setName] = React.useState(vendor.name)
  const [addressLine1, setAddressLine1] = React.useState(vendor.addressLine1 ?? '')
  const [addressLine2, setAddressLine2] = React.useState(vendor.addressLine2 ?? '')
  const [city, setCity] = React.useState(vendor.city ?? '')
  const [state, setState] = React.useState(vendor.state ?? '')
  const [zip, setZip] = React.useState(vendor.zip ?? '')
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  function edited<T>(setter: (v: T) => void): (v: T) => void {
    return (v: T) => {
      setter(v)
      setSaved(false)
    }
  }

  async function handleSave(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSaved(false)
    const parsed = updateVendorInputSchema.safeParse({ name, addressLine1, addressLine2, city, state, zip })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input')
      return
    }
    try {
      await updateVendor.mutateAsync(parsed.data)
      setSaved(true)
    } catch (err) {
      setError(requestErrorMessage(err))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vendor details</CardTitle>
        <CardDescription>Your business name and mailing address, as they appear across the platform.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSave} noValidate>
          <Field label="Business name" htmlFor="vendor-name">
            <Input id="vendor-name" value={name} onChange={(e) => edited(setName)(e.target.value)} placeholder="Acme Rentals" />
          </Field>
          <Field label="Address line 1" htmlFor="vendor-addr1">
            <Input id="vendor-addr1" value={addressLine1} onChange={(e) => edited(setAddressLine1)(e.target.value)} placeholder="123 Market St" />
          </Field>
          <Field label="Address line 2" htmlFor="vendor-addr2">
            <Input id="vendor-addr2" value={addressLine2} onChange={(e) => edited(setAddressLine2)(e.target.value)} placeholder="Suite 500 (optional)" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="City" htmlFor="vendor-city" className="sm:col-span-1">
              <Input id="vendor-city" value={city} onChange={(e) => edited(setCity)(e.target.value)} placeholder="Austin" />
            </Field>
            <Field label="State" htmlFor="vendor-state">
              <Input id="vendor-state" value={state} onChange={(e) => edited(setState)(e.target.value)} placeholder="TX" />
            </Field>
            <Field label="ZIP" htmlFor="vendor-zip">
              <Input id="vendor-zip" value={zip} onChange={(e) => edited(setZip)(e.target.value)} placeholder="78701" />
            </Field>
          </div>
          <FormActions align="start">
            <Button pending={updateVendor.isPending} type="submit" disabled={name.trim() === ''}>
              <Check className="h-4 w-4" />
              {updateVendor.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            {error ? <FormError>{error}</FormError> : null}
            {saved && !error ? <p className="text-sm text-ok-ink">Saved.</p> : null}
          </FormActions>
        </form>
      </CardContent>
    </Card>
  )
}

/** How far the vendor travels — drives which nearby venues it can discover. Uses
 * the geocoded business address as the origin. */
/**
 * The vendor's own cover photograph. Self-service: a vendor manages its own
 * profile, so this sits with its address and service area rather than behind a
 * platform admin.
 */
function VendorPhoto({ vendor }: { vendor: VendorDto }) {
  const update = useUpdateOwnVendor()
  return (
    <Card>
      <CardHeader>
        <CardTitle>Photo</CardTitle>
        <CardDescription>The photograph that represents your business wherever it is listed.</CardDescription>
      </CardHeader>
      <CardContent>
        <CoverPhotoField
          coverImageUrl={vendor.coverImageUrl}
          kind="vendor"
          onSave={async (coverImageUrl) => {
            await update.mutateAsync({ coverImageUrl })
          }}
        />
      </CardContent>
    </Card>
  )
}

function VendorServiceArea({ vendor }: { vendor: VendorDto }) {
  const update = useUpdateOwnVendor()
  const [radius, setRadius] = React.useState(vendor.serviceRadiusMiles?.toString() ?? '')
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const located = vendor.latitude !== null && vendor.longitude !== null

  async function save(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSaved(false)
    const trimmed = radius.trim()
    const parsed = updateVendorInputSchema.safeParse({
      serviceRadiusMiles: trimmed === '' ? null : Number.parseInt(trimmed, 10),
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a radius between 0 and 500 miles.')
      return
    }
    try {
      await update.mutateAsync(parsed.data)
      setSaved(true)
    } catch (err) {
      setError(requestErrorMessage(err))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service area</CardTitle>
        <CardDescription>
          How far you&rsquo;ll travel from your business address, used to find venues near you to request.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-ink-muted">
          {located
            ? 'Your business address is located — venues within your radius will show up under Find venues.'
            : 'We couldn’t locate your business address. Add a complete street address above so we can find nearby venues.'}
        </p>
        <form className="flex items-end gap-3" onSubmit={save} noValidate>
          <Field label="Travel radius (miles)" htmlFor="vendor-radius" className="w-48">
            <Input
              id="vendor-radius"
              type="number"
              min={0}
              max={500}
              value={radius}
              onChange={(e) => {
                setRadius(e.target.value)
                setSaved(false)
              }}
              placeholder="e.g. 50"
            />
          </Field>
          <Button pending={update.isPending} type="submit">
            <Check className="h-4 w-4" />
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
        {error ? <FormError>{error}</FormError> : null}
        {saved && !error ? <p className="text-sm text-ok-ink">Saved.</p> : null}
      </CardContent>
    </Card>
  )
}

/**
 * Whether venues can find the company when they look for vendors. Everyone at
 * the company sees it; only an owner can change it. Venues the company already
 * works with see it either way (issue #73).
 */
export function VendorSearchListing({ vendor }: { vendor: VendorDto }) {
  const { user } = useAuth()
  const people = useVendorPeople({ kind: 'own' })
  const setListing = useSetOwnVendorSearchListing()
  const [error, setError] = React.useState<string | null>(null)
  const isOwner = people.data?.users.find((person) => person.id === user?.id)?.isOwner === true
  const listed = !vendor.hiddenFromSearch

  async function choose(next: boolean): Promise<void> {
    setError(null)
    try {
      await setListing.mutateAsync({ hidden: !next })
    } catch (err) {
      setError(requestErrorMessage(err))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Venue search</CardTitle>
        <CardDescription>
          Venues on FLOH Venues look for vendors by name and by what they offer. They see your company&rsquo;s
          name and services, never your people or the other venues you work with.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={listed}
            disabled={!isOwner || setListing.isPending}
            onChange={(e) => void choose(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-line"
          />
          <span className="space-y-1">
            <span className="block font-medium text-ink">Venues can find {vendor.name}</span>
            <span className="block text-ink-muted">
              {listed
                ? 'Shown when a venue searches for vendors.'
                : 'Not shown when venues search. Venues you already work with still see you.'}
            </span>
            {people.isSuccess && !isOwner ? (
              <span className="block text-ink-muted">Only an owner can change this.</span>
            ) : null}
            {error ? <span className="block text-danger-ink">{error}</span> : null}
          </span>
        </label>
      </CardContent>
    </Card>
  )
}

/** The services this vendor offers. Offering a service unlocks its configuration
 * (e.g. `furniture` reveals the furniture library) and lets venues approve it. */
function VendorServices({ vendor }: { vendor: VendorDto }) {
  const setServices = useSetOwnVendorServices()
  return (
    <Card>
      <CardHeader>
        <CardTitle>Services offered</CardTitle>
        <CardDescription>
          What your business provides. Offering <strong>Furniture</strong> unlocks your furniture library; a venue then
          approves each service before it&rsquo;s available there. Turning one off hides its setup but never deletes it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <VendorServicesPicker
          value={vendor.services}
          busy={setServices.isPending}
          onSave={async (services) => {
            await setServices.mutateAsync(services)
          }}
        />
      </CardContent>
    </Card>
  )
}

/**
 * Who quote requests are emailed to (docs/vendor-growth.spec.md, "Quote
 * requests"): someone at the company who has joined, or its owners. Anyone
 * there may choose, as anyone edits the company.
 */
export function VendorQuoteRecipient({ vendor }: { vendor: VendorDto }) {
  const people = useVendorPeople({ kind: 'own' })
  const setRecipient = useSetQuoteRecipient()
  const joined = (people.data?.users ?? []).filter((person) => person.status === 'ACTIVE')
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quote requests</CardTitle>
        <CardDescription>Who gets quote requests by email. Everyone at {vendor.name} sees them under Requests.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Field label="Email them to" htmlFor="quote-recipient" className="max-w-sm">
          <Select
            id="quote-recipient"
            value={vendor.quoteRecipientId ?? ''}
            disabled={people.isPending || setRecipient.isPending}
            onChange={(e) => setRecipient.mutate(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">The owners</option>
            {joined.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
        </Field>
        {setRecipient.isError ? <FormError>{requestErrorMessage(setRecipient.error)}</FormError> : null}
      </CardContent>
    </Card>
  )
}

/** A vendor user's home: manage their vendor's details and see their linked venues. */
export function VendorPage() {
  const vendorQuery = useOwnVendor()

  function renderContent() {
    if (vendorQuery.isPending) {
      return (
        <LoadingState label="Loading vendor…" />
      )
    }
    if (vendorQuery.isError) {
      return (
        <ErrorState message={requestErrorMessage(vendorQuery.error)} onRetry={() => void vendorQuery.refetch()} />
      )
    }
    return (
      <>
        <VendorSettings vendor={vendorQuery.data} />
        <VendorPhoto vendor={vendorQuery.data} />
        <VendorServiceArea vendor={vendorQuery.data} />
        <VendorServices vendor={vendorQuery.data} />
        <VendorSearchListing vendor={vendorQuery.data} />
        <VendorQuoteRecipient vendor={vendorQuery.data} />
      </>
    )
  }

  return (
    <PageContainer className="space-y-8">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-past-soft">
          <Store className="h-5 w-5 text-ink-muted" />
        </div>
        <PageHeader
          title={vendorQuery.data?.name ?? 'Your vendor'}
          description="Manage your vendor’s details and furniture."
        />
      </div>
      {renderContent()}
    </PageContainer>
  )
}
